// src/utils/dateFormatters.ts
// Locale-aware date formatting, ported from the full planner (Norway.Booking.S3).
// Light has no userStore, so the locale source of truth here is the custom i18n
// `getCurrentLocale()` (S3 falls back to userStore.language first). All formatting is
// pinned to the Norwegian timezone so a user's device timezone can't shift the day.

import { getCurrentLocale } from '@/i18n'

// ============================================================================
// Locale Helpers
// ============================================================================

/**
 * Current app locale. In S3 this prefers userStore.language; Light has no user
 * store, so the i18n locale is the sole source (defaulting to 'no').
 */
function getAppLocale(): 'en' | 'no' {
  return getCurrentLocale() || 'no'
}

/**
 * Map app locale to Intl locale codes.
 */
function getIntlLocale(): string {
  return getAppLocale() === 'no' ? 'nb-NO' : 'en-US'
}

// Constants
const NORWEGIAN_TIMEZONE = 'Europe/Oslo'
const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/

function formatInOslo(date: Date, locale: string, options: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat(locale, {
    ...options,
    timeZone: NORWEGIAN_TIMEZONE,
  }).format(date)
}

function isValidDateOnly(dateString: string): boolean {
  if (!DATE_ONLY_REGEX.test(dateString)) {
    return false
  }

  const [year, month, day] = dateString.split('-').map(Number)
  const date = new Date(Date.UTC(year, month - 1, day))

  return (
    date.getUTCFullYear() === year && date.getUTCMonth() + 1 === month && date.getUTCDate() === day
  )
}

export function isValidDateString(dateStr: string | null | undefined): boolean {
  if (!dateStr || typeof dateStr !== 'string') return false
  return isValidDateOnly(dateStr)
}

// ============================================================================
// Date Parsing Helpers
// ============================================================================

/**
 * Extracts a date from a timestamp while preserving Norwegian timezone.
 */
export function extractNorwegianDate(timestamp: string): Date {
  if (!timestamp) {
    return new Date()
  }

  try {
    if (isValidDateOnly(timestamp)) {
      const [year, month, day] = timestamp.split('-').map(Number)
      return new Date(Date.UTC(year, month - 1, day, 12, 0, 0))
    }

    const result = new Date(timestamp)

    if (isNaN(result.getTime())) {
      console.error('Invalid date result for timestamp:', timestamp)
      return new Date()
    }

    return result
  } catch (error) {
    console.error('Error parsing date:', error)
    return new Date()
  }
}

// ============================================================================
// Date Formatting Functions
// ============================================================================

/**
 * Formats a date as "24. jun" (short format).
 * Accepts both Date objects and date strings (YYYY-MM-DD or ISO timestamps).
 *
 * Norwegian: "20. jan" (with period after day, lowercase month, no month period)
 * English:   "20 Jan" (no period, capitalized)
 */
export function formatDateShort(date: Date | string | null | undefined): string {
  if (!date) {
    return ''
  }

  try {
    const dateObj = date instanceof Date ? date : extractNorwegianDate(date)

    if (isNaN(dateObj.getTime())) {
      console.error('Invalid date encountered:', date)
      return ''
    }

    const appLocale = getAppLocale()
    const intlLocale = getIntlLocale()
    const day = formatInOslo(dateObj, intlLocale, {
      day: 'numeric',
    })
    const monthName = formatInOslo(dateObj, intlLocale, {
      month: 'short',
    })

    if (appLocale === 'no') {
      const normalizedDay = day.replace(/\./g, '')
      const normalizedMonth = monthName.replace(/\./g, '').toLowerCase()
      return `${normalizedDay}. ${normalizedMonth}`
    }
    return `${day} ${monthName}`
  } catch (error) {
    console.error('Error formatting date:', error)
    return ''
  }
}

/**
 * Formats a date as "24. juni" (medium format with day and full month name).
 */
export function formatDateMedium(dateTime: string | undefined): string {
  if (!dateTime) return ''

  const locale = getIntlLocale()
  const date = extractNorwegianDate(dateTime)

  if (isNaN(date.getTime())) {
    return ''
  }

  return formatInOslo(date, locale, {
    day: 'numeric',
    month: 'long',
  })
}
